/* ─────────────────────────────────────────────
   PeopleSuiteNavbar.ts
   Glassmorphism navbar - TypeScript implementation
───────────────────────────────────────────── */

import {
  DATE_REFRESH_INTERVAL_MS,
  DEFAULT_NOTIF_DOT_COLOR,
  DEFAULT_PAGE_NAME,
  DEFAULT_USER_EMAIL,
  DEFAULT_USER_NAME,
  DEFAULT_USER_ROLE,
  DOM_ID,
  EVENT_NAV_CLICK,
  EVENT_SIDEBAR_TOGGLE,
  GLOW_START_DELAY_MS,
  MONTH_NAMES_SHORT,
  TOAST_DISPLAY_DURATION_MS,
  TOAST_EXIT_ANIMATION_MS,
  TOAST_ICON_DEFAULT,
  TOAST_ICON_NOTIFICATIONS,
  TOAST_ICON_SIGN_OUT,
  TOAST_MSG_NOTIF_OPENED,
  TOAST_MSG_SIGNING_OUT,
  WEEKDAY_NAMES,
  formatRoleLabel,
} from './PeopleSuiteNavbar.constants.ts';
import './PeopleSuiteNavbar.scss';

export interface NavbarOptions {
  onNavigate?: (path: string) => void;
  onSignOut?: () => void;
  getPageName?: () => string;
  userName?: string;
  userEmail?: string;
  userRole?: string;
}

/* ── State ── */

let profileOpen: boolean = false;
let notifOpen: boolean = false;
let sidebarCollapsed: boolean = false;

/* ── Notification state ── */
let cachedNotifItems: NotifItem[] = [];
const dismissedNotifIds = new Set<string>();
let onNotifNavigate: ((path: string) => void) | undefined;

const DISMISSED_NOTIFS_STORAGE_KEY = 'hrms_dismissed_notifications';

interface DismissedNotifRecord {
  count?: number;
  time: number;
}

function getStoredDismissedMap(): Record<string, DismissedNotifRecord> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_NOTIFS_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, DismissedNotifRecord>;
  } catch {
    return {};
  }
}

function persistDismissedRecord(id: string, count?: number): void {
  try {
    const map = getStoredDismissedMap();
    map[id] = { count, time: Date.now() };
    sessionStorage.setItem(DISMISSED_NOTIFS_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

export function dismissNotification(id: string, count?: number): void {
  const actualCount = count ?? cachedNotifItems.find((i) => i.id === id)?.count;
  dismissedNotifIds.add(id);
  persistDismissedRecord(id, actualCount);
  renderNotifBody();
}

export function clearDismissedNotifications(): void {
  dismissedNotifIds.clear();
  try {
    sessionStorage.removeItem(DISMISSED_NOTIFS_STORAGE_KEY);
    localStorage.removeItem(DISMISSED_NOTIFS_STORAGE_KEY);
  } catch {}
  renderNotifBody();
}

/* ── Typed DOM refs ── */
let hamburgerBtn!: HTMLButtonElement;
let breadcrumbActive!: HTMLSpanElement;
let dateEl!: HTMLSpanElement;
let bellBtn!: HTMLButtonElement;
let notifDropdown!: HTMLDivElement;
let avatarBtn!: HTMLDivElement;
let profileDropdown!: HTMLDivElement;
let toastContainer!: HTMLDivElement;
let navBrand!: HTMLSpanElement;

/* ── Helpers ── */

function formatDate(date: Date): string {
  const day: string = WEEKDAY_NAMES[date.getDay()];
  const dd: string = String(date.getDate()).padStart(2, '0');
  const mon: string = MONTH_NAMES_SHORT[date.getMonth()];
  const yyyy: number = date.getFullYear();
  return `${day}, ${dd} ${mon} ${yyyy}`;
}

function showToast(msg: string, icon: string = TOAST_ICON_DEFAULT): void {
  const toast: HTMLDivElement = document.createElement('div');
  toast.className = 'ps-toast';
  toast.innerHTML = `<span class="material-symbols-outlined ps-toast-icon">${icon}</span><span>${msg}</span>`;
  toastContainer.appendChild(toast);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('show'));
  });

  const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
    toast.classList.remove('show');
    const removeTimer: ReturnType<typeof setTimeout> = setTimeout(
      () => toast.remove(),
      TOAST_EXIT_ANIMATION_MS
    );
    void removeTimer;
  }, TOAST_DISPLAY_DURATION_MS);
  void timer;
}

function toggleSidebar(): void {
  sidebarCollapsed = !sidebarCollapsed;
  hamburgerBtn.classList.toggle('collapsed', sidebarCollapsed);
  window.dispatchEvent(new CustomEvent(EVENT_SIDEBAR_TOGGLE));
}

function toggleProfile(): void {
  profileOpen = !profileOpen;
  notifOpen = false;
  profileDropdown.classList.toggle('open', profileOpen);
  notifDropdown.classList.remove('open');
}

function toggleNotif(): void {
  notifOpen = !notifOpen;
  profileOpen = false;
  notifDropdown.classList.toggle('open', notifOpen);
  profileDropdown.classList.remove('open');
  if (notifOpen) showToast(TOAST_MSG_NOTIF_OPENED, TOAST_ICON_NOTIFICATIONS);
}

/* ── Brand glow controls ── */

function triggerGlow(): void {
  if (!navBrand) return;
  navBrand.classList.remove('glow-active');
  navBrand.classList.add('glow-once');
  const onEnd = (e: AnimationEvent): void => {
    if (e.animationName !== 'logoPulse') return;
    navBrand.classList.remove('glow-once');
    navBrand.classList.add('glow-active');
    navBrand.removeEventListener('animationend', onEnd);
  };
  navBrand.addEventListener('animationend', onEnd);
}

function startGlow(): void {
  if (!navBrand) return;
  navBrand.classList.remove('glow-once');
  navBrand.classList.add('glow-active');
}

function stopGlow(): void {
  if (!navBrand) return;
  navBrand.classList.remove('glow-active', 'glow-once');
}

function closeAllDropdowns(): void {
  profileOpen = false;
  notifOpen = false;
  profileDropdown.classList.remove('open');
  notifDropdown.classList.remove('open');
}

/* ── HTML template ── */
function buildNavHTML(
  pageName: string,
  userName: string = DEFAULT_USER_NAME,
  userEmail: string = DEFAULT_USER_EMAIL,
  userRole: string = DEFAULT_USER_ROLE
): string {
  return `
    <!-- Layer 1: animated gradient -->
    <div class="ps-nav-gradient"></div>

    <!-- Layer 2: glassmorphism bar -->
    <nav class="ps-topnav">

      <!-- Left: toggle + breadcrumb -->
      <div class="ps-nav-left">
        <button class="ps-hamburger-btn" id="${DOM_ID.hamburgerBtn}" aria-label="Toggle sidebar" data-testid="navbar-toggle-sidebar-button">
          <span class="ps-hamburger-line"></span>
          <span class="ps-hamburger-line"></span>
          <span class="ps-hamburger-line"></span>
          <span class="ps-hamburger-line"></span>
        </button>
        <div class="ps-breadcrumb">
          <button class="ps-breadcrumb-home" id="${DOM_ID.breadcrumbHome}" data-testid="navbar-breadcrumb-home">Home</button>
          <span class="ps-breadcrumb-sep">›</span>
          <span class="ps-breadcrumb-active" id="${DOM_ID.breadcrumbActive}">${pageName}</span>
        </div>
      </div>

      <!-- Center: brand -->
      <div class="ps-nav-center">
        <span id="${DOM_ID.navBrand}" class="nav-brand">PeopleSuite</span>
      </div>

      <!-- Right: date, bell, dark toggle, admin, avatar -->
      <div class="ps-nav-right">
        <span class="ps-date" id="${DOM_ID.date}"></span>

        <!-- Bell -->
        <div class="ps-bell-wrap">
          <button class="ps-bell-btn" id="${DOM_ID.bellBtn}" aria-label="Notifications" data-testid="navbar-notifications-button">
            <span class="material-symbols-outlined">notifications</span>
          </button>
          <span class="ps-bell-badge" id="${DOM_ID.bellBadge}" style="display:none">0</span>
          <div class="ps-notif-dropdown" id="${DOM_ID.notifDropdown}">
            <div class="ps-notif-header">Notifications</div>
            <div id="${DOM_ID.notifBody}">
              <div class="ps-notif-empty">No new notifications</div>
            </div>
          </div>
        </div>

        <!-- Role pill -->
        <button class="ps-admin-btn" aria-label="Role" data-testid="navbar-role-pill">${formatRoleLabel(userRole)}</button>

        <!-- Avatar -->
        <div class="ps-avatar-wrap">
          <div class="ps-avatar-btn" id="${DOM_ID.avatarBtn}" data-testid="user-menu" role="button" aria-label="Profile menu">
            <span class="material-symbols-outlined">person</span>
          </div>
          <span class="ps-gold-dot"></span>
          <div class="ps-profile-dropdown" id="${DOM_ID.profileDropdown}">
            <div class="ps-dropdown-header">
              <span class="material-symbols-outlined">account_circle</span>
              <div class="ps-dropdown-user-info">
                <span class="ps-dropdown-username">${userName}</span>
                ${userEmail ? `<span class="ps-dropdown-email">${userEmail}</span>` : ''}
              </div>
            </div>
            <div class="ps-dropdown-divider"></div>
            <div class="ps-dropdown-item" id="${DOM_ID.profileItem}" data-testid="profile-button">
            <span class="material-symbols-outlined">person</span>Profile
            </div>
            <div class="ps-dropdown-item ps-danger" id="${DOM_ID.signOutItem}" data-testid="logout-button">
              <span class="material-symbols-outlined">logout</span>Sign Out
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Toast container -->
    <div class="ps-toast-container" id="${DOM_ID.toastContainer}"></div>
  `;
}

/* ── Mount function ── */
export function mountNavbar(navContainer: HTMLElement, opts: NavbarOptions = {}): () => void {
  /* Store navigate callback so renderNotifBody click handler can use it */
  onNotifNavigate = opts.onNavigate;

  /* Inject HTML */
  const pageName: string = opts.getPageName ? opts.getPageName() : DEFAULT_PAGE_NAME;
  const userName: string = opts.userName ?? DEFAULT_USER_NAME;
  const userEmail: string = opts.userEmail ?? DEFAULT_USER_EMAIL;
  const userRole: string = opts.userRole ?? DEFAULT_USER_ROLE;
  navContainer.className = 'ps-nav-wrapper';
  navContainer.innerHTML = buildNavHTML(pageName, userName, userEmail, userRole);

  /* Bind typed DOM refs */
  hamburgerBtn = navContainer.querySelector<HTMLButtonElement>(`#${DOM_ID.hamburgerBtn}`)!;
  breadcrumbActive = navContainer.querySelector<HTMLSpanElement>(`#${DOM_ID.breadcrumbActive}`)!;
  dateEl = navContainer.querySelector<HTMLSpanElement>(`#${DOM_ID.date}`)!;
  bellBtn = navContainer.querySelector<HTMLButtonElement>(`#${DOM_ID.bellBtn}`)!;
  notifDropdown = navContainer.querySelector<HTMLDivElement>(`#${DOM_ID.notifDropdown}`)!;
  avatarBtn = navContainer.querySelector<HTMLDivElement>(`#${DOM_ID.avatarBtn}`)!;
  profileDropdown = navContainer.querySelector<HTMLDivElement>(`#${DOM_ID.profileDropdown}`)!;
  toastContainer = navContainer.querySelector<HTMLDivElement>(`#${DOM_ID.toastContainer}`)!;
  navBrand = document.getElementById(DOM_ID.navBrand) as HTMLSpanElement;

  /* Set initial date */
  if (dateEl) {
    dateEl.textContent = formatDate(new Date());
  }

  /* ── Event listeners ── */

  if (hamburgerBtn) {
    hamburgerBtn.addEventListener('click', (_e: MouseEvent): void => {
      toggleSidebar();
    });
  }

  const homeBtn: HTMLButtonElement = navContainer.querySelector<HTMLButtonElement>(
    `#${DOM_ID.breadcrumbHome}`
  )!;
  homeBtn.addEventListener('click', (_e: MouseEvent): void => {
    if (opts.onNavigate) opts.onNavigate('/dashboard');
  });

  bellBtn.addEventListener('click', (_e: MouseEvent): void => {
    toggleNotif();
  });

  /* Delegated click on notification items — mark as read and navigate */
  notifDropdown.addEventListener('click', (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    const item = target.closest<HTMLElement>('[data-notif-id]');
    if (!item) return;

    const id = item.dataset.notifId!;
    const link = item.dataset.notifLink;
    const countVal = item.dataset.notifCount;
    const count = countVal ? parseInt(countVal, 10) : undefined;

    dismissNotification(id, count);
    closeAllDropdowns();

    if (link && onNotifNavigate) {
      onNotifNavigate(link);
    }
  });

  avatarBtn.addEventListener('click', (_e: MouseEvent): void => {
    toggleProfile();
  });
  const profileItem: HTMLDivElement = navContainer.querySelector<HTMLDivElement>(
  `#${DOM_ID.profileItem}`
)!;
profileItem.addEventListener('click', (_e: MouseEvent): void => {
  closeAllDropdowns();
  if (opts.onNavigate) opts.onNavigate('/profile');
});

  const signOutItem: HTMLDivElement = navContainer.querySelector<HTMLDivElement>(
  `#${DOM_ID.signOutItem}`
)!;
signOutItem.addEventListener('click', (_e: MouseEvent): void => {
  closeAllDropdowns();
  showToast(TOAST_MSG_SIGNING_OUT, TOAST_ICON_SIGN_OUT);
  if (opts.onSignOut) opts.onSignOut();
});
  
  /* Outside-click closes dropdowns */
  const outsideClickHandler = (e: MouseEvent): void => {
    const target = e.target as Node;
    if (!avatarBtn.contains(target) && !profileDropdown.contains(target)) {
      profileOpen = false;
      profileDropdown.classList.remove('open');
    }
    if (!bellBtn.contains(target) && !notifDropdown.contains(target)) {
      notifOpen = false;
      notifDropdown.classList.remove('open');
    }
  };
  document.addEventListener('mousedown', outsideClickHandler);

  /* Date ticker — updates every minute */
  const dateInterval: ReturnType<typeof setInterval> = setInterval((): void => {
    dateEl.textContent = formatDate(new Date());
  }, DATE_REFRESH_INTERVAL_MS);

  /* ── Start continuous glow after short delay ── */
  const glowTimer: ReturnType<typeof setTimeout> = setTimeout(startGlow, GLOW_START_DELAY_MS);

  /* ── Trigger one glow flash on sidebar nav click ── */
  const onNavClick = (_e: Event): void => {
    triggerGlow();
  };
  window.addEventListener(EVENT_NAV_CLICK, onNavClick);

  /* ── Return cleanup ── */
  return (): void => {
    document.removeEventListener('mousedown', outsideClickHandler);
    clearInterval(dateInterval);
    clearTimeout(glowTimer);
    window.removeEventListener(EVENT_NAV_CLICK, onNavClick);
    stopGlow();
  };
}

/* ── Breadcrumb updater (call on route change) ── */
export function updateBreadcrumb(pageName: string): void {
  if (breadcrumbActive) breadcrumbActive.textContent = pageName;
}

/* ── Notification updater ── */
export interface NotifItem {
  /** Stable unique key — used to track read/dismissed state within the session */
  id: string;
  msg: string;
  /** Label shown below the message; rendered as a clickable link when `link` is set */
  time: string;
  /** Route to navigate to when the item or its action link is clicked */
  link?: string;
  color?: string;
  count?: number;
}

function isItemDismissed(n: NotifItem): boolean {
  const map = getStoredDismissedMap();
  const record = map[n.id];
  if (record) {
    if (typeof record.count === 'number' && typeof n.count === 'number') {
      if (n.count > record.count) {
        dismissedNotifIds.delete(n.id);
        delete map[n.id];
        try {
          sessionStorage.setItem(DISMISSED_NOTIFS_STORAGE_KEY, JSON.stringify(map));
        } catch {}
        return false;
      }
      return true;
    }
    return true;
  }
  return dismissedNotifIds.has(n.id);
}

function renderNotifBody(): void {
  const badge = document.getElementById(DOM_ID.bellBadge);
  const body = document.getElementById(DOM_ID.notifBody);
  if (!badge || !body) return;

  const unread = cachedNotifItems.filter((n) => !isItemDismissed(n));

  badge.textContent = String(unread.length);
  badge.style.display = unread.length > 0 ? 'flex' : 'none';

  if (unread.length === 0) {
    body.innerHTML = '<div class="ps-notif-empty">No new notifications</div>';
  } else {
    body.innerHTML = unread
      .map(
        (n) => `
      <div class="ps-notif-item" data-notif-id="${n.id}" data-notif-count="${n.count ?? ''}" ${n.link ? `data-notif-link="${n.link}"` : ''}>
        <div class="ps-notif-dot" style="background:${n.color ?? DEFAULT_NOTIF_DOT_COLOR}"></div>
        <div>
          <div class="ps-notif-msg">${n.msg}</div>
          ${
            n.link
              ? `<div class="ps-notif-action" data-notif-id="${n.id}" data-notif-count="${n.count ?? ''}" data-notif-link="${n.link}">${n.time}</div>`
              : `<div class="ps-notif-time">${n.time}</div>`
          }
        </div>
      </div>
    `
      )
      .join('');
  }
}

export function updateNotifications(items: NotifItem[]): void {
  cachedNotifItems = items;
  renderNotifBody();
}

